import React, { useEffect, useState } from "react";
import { Avatar, Row, Col, Typography, Card, Space, Divider } from "antd";
import { CalendarOutlined, MailOutlined, HomeOutlined, HeartOutlined, NumberOutlined } from "@ant-design/icons";
import PatientDetails from "../../../models/patientDetails";
import BaseService from "../../../service/base.service";
import { useIsMobile } from "../../../hooks/useIsMobile";

const { Title, Text } = Typography;

type PatientProfileCardProps = {
    patientId: string;
};

const PatientProfileCard: React.FC<PatientProfileCardProps> = ({ patientId }) => {
    const isMobile = useIsMobile();
    const [patientDetails, setPatientDetails] = useState<PatientDetails>();

    useEffect(() => {
        handleGetPatientDetails();
    }, []);

    const handleGetPatientDetails = async () => {
        try {
            const rp = await BaseService.get<PatientDetails>("/patients/", patientId);
            if (rp.Status) {
                const data = rp.Data;
                setPatientDetails(
                    new PatientDetails(
                        data.id,
                        data.firstName,
                        data.lastName,
                        data.birthDate,
                        data.phoneNumber,
                        data.email,
                        data.address,
                        data.createdAt,
                        data.modifiedAt,
                        data.bloodType,
                        data.age,
                        data.nrOfExaminations
                    )
                );
            } else {
                console.error("Stats error:", rp.Messages, rp.Exception);
            }
        } finally { }
    };

    const details: {
        label: string;
        value: React.ReactNode;
        icon?: React.ReactNode;
        valueStyle?: React.CSSProperties;
    }[] = [
            {
                label: "Birth Date",
                value: patientDetails?.birthDate,
                icon: <CalendarOutlined style={{ marginRight: 6 }} />,
            },
            { label: "Age", value: patientDetails?.age },
            {
                label: "Blood Type",
                value: patientDetails?.bloodType,
                icon: <HeartOutlined style={{ marginRight: 6 }} />,
            },
            { label: "Status", value: "Active", valueStyle: { color: "#52c41a" } },
            { label: "Created", value: patientDetails?.createdAt },
            { label: "Modified", value: patientDetails?.modifiedAt ?? "N/A" },
            {
                label: "Examinations",
                value: patientDetails?.nrOfExaminations ?? "0",
                icon: <NumberOutlined style={{ marginRight: 6 }} />,
            },
            { label: "Email", value: patientDetails?.email || "N/A" },
            {
                label: "Address",
                value: patientDetails?.address ?? "N/A",
                icon: <HomeOutlined style={{ marginRight: 6 }} />,
            },
        ];

    return (
        <Card
            style={{
                borderRadius: isMobile ? 14 : 20,
                padding: isMobile ? 12 : 24,
                background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)",
                marginBottom: isMobile ? 16 : 32,
            }}
            styles={{ body: { padding: 0 } }}
        >
            <Row gutter={[isMobile ? 16 : 32, 24]} align="middle">
                <Col xs={24} md={6} style={{ textAlign: "center" }}>
                    <div
                        style={{
                            display: "inline-block",
                            borderRadius: "50%",
                            padding: 4,
                            background: "linear-gradient(135deg, #1677ff, #4096ff)",
                        }}
                    >
                        <Avatar
                            size={isMobile ? 72 : 100}
                            style={{
                                backgroundColor: "#fff",
                                color: "#1677ff",
                                fontSize: isMobile ? 28 : 36,
                                fontWeight: 600,
                            }}
                        >
                            {`${patientDetails?.firstName?.[0] ?? ""}${patientDetails?.lastName?.[0] ?? ""}`.toUpperCase()}
                        </Avatar>
                    </div>

                    <div style={{ marginTop: isMobile ? 10 : 16 }}>
                        <Title level={4} style={{ marginBottom: 4, fontSize: isMobile ? 16 : undefined }}>
                            Mrs. {patientDetails?.firstName} {patientDetails?.lastName}
                        </Title>
                        <Text type="secondary">
                            <MailOutlined style={{ marginRight: 6 }} />
                            {patientDetails?.email || "N/A"}
                        </Text>
                    </div>
                </Col>

                {/* Right side - Details */}
                <Col xs={24} md={18}>
                    <Row gutter={[16, 16]}>
                        {details.map((detail, index) => (
                            <Col
                                xs={12}
                                sm={isMobile ? 12 : 8}
                                key={detail.label}
                                style={{
                                    // 2-column grid on mobile: align the second column to the card edge
                                    textAlign: isMobile && index % 2 === 1 ? "right" : "left",
                                }}
                            >
                                <Text type="secondary">
                                    {detail.icon}
                                    {detail.label}
                                </Text>
                                <div style={{ wordBreak: "break-word" }}>
                                    <strong style={detail.valueStyle}>{detail.value}</strong>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Col>
            </Row>
            <Divider style={{ margin: "24px 0 0 0" }} />
            <div style={{ textAlign: "center", color: "#8c8c8c", fontSize: 13, marginTop: 12 }}>
                Patient profile — synced with latest examination data
            </div>
        </Card>
    );
};

export default PatientProfileCard;
